import { Artist } from "./library-objects";
import { Project, ProjectSubmission } from "./project-objects";

export const allowedRoles = ["ConductionAdmin", "ClassroomAdmin", "Student", "Guest", "User", "Transcriber", "Instrumentalist", "Artist"] as const;
export type Role = (typeof allowedRoles)[number];

export class User {
	public readonly id: string;
	public readonly username: string;
	public readonly email: string | null;
	public readonly dateCreated: Date | undefined;
	public readonly roles: Role[];
	public readonly classroom: Classroom | undefined;
	public readonly projectIds: string[];
	public readonly completedBeginnerTutorial: boolean;
	public readonly artists: Artist[];

	private constructor(id: string, username: string, email: string | undefined, dateCreated: Date, roles: Role[], classroom: Classroom | undefined, projectIds: string[], completedBeginnerTutorial: boolean, artists: Artist[]) {
		this.id = id;
		this.username = username;
		this.email = email || null;
		this.dateCreated = dateCreated;
		this.roles = roles;
		this.classroom = classroom;
		this.projectIds = projectIds;
		this.completedBeginnerTutorial = completedBeginnerTutorial;
		this.artists = artists;
	}

	public static fromJson(json: any): User {
		return new User(
			json.userId,
			json.userName,
			json.email,
			new Date(json.dateCreated),
			json.roles?.filter((role: string) => allowedRoles.includes(role as Role)) ?? [],
			json.classroom ? Classroom.fromJson(json.classroom) : undefined,
			json.projectIds,
			json.completedBeginnerTutorial,
			json.artists.map((artist: any) => Artist.fromJson(artist))
		);
	}

	public static getGuestUser() {
		return new User(
			"guest",
			"Guest",
			undefined,
			new Date(),
			["Guest"],
			undefined,
			[],
			false,
			[]
		);
	}
}

export class Classroom {
	private classroomAdmins: User[] | undefined;
	private students: User[] | undefined;
	private projects: Project[] | undefined;

	public readonly id: string;
	public readonly name: string;
	public readonly desiredName: string;
	public readonly dateCreated: Date;
	public readonly classroomAdminLimit: number;
	public readonly desiredClassroomAdminLimit: number;
	public readonly studentLimit: number;
	public readonly desiredStudentLimit: number;
	public readonly projectCount: number;
	public readonly submissionCount: number;
	public readonly tokens: ProjectSubmissionToken[] | undefined;
	public readonly defaultSongIds: string[];
	public readonly classroomAdminAccessCode: string | undefined;
	public readonly studentAccessCode: string | undefined;

	private constructor(
		id: string,
		name: string,
		dateCreated: Date,
		classroomAdminLimit: number,
		studentLimit: number,
		projectCount: number,
		submissionCount: number,
		tokens: ProjectSubmissionToken[] | undefined,
		defaultSongIds: string[],
		classroomAdminAccessCode?: string,
		studentAccessCode?: string,
		desiredClassroomAdminLimit?: number,
		desiredStudentLimit?: number,
		desiredName?: string
	) {
		this.id = id;
		this.name = name;
		this.desiredName = desiredName ?? name;
		this.dateCreated = dateCreated;
		this.classroomAdminLimit = classroomAdminLimit;
		this.studentLimit = studentLimit;
		this.desiredClassroomAdminLimit = desiredClassroomAdminLimit ?? classroomAdminLimit;
		this.desiredStudentLimit = desiredStudentLimit ?? studentLimit;
		this.projectCount = projectCount;
		this.submissionCount = submissionCount;
		this.tokens = tokens;
		this.defaultSongIds = defaultSongIds;
		this.classroomAdminAccessCode = classroomAdminAccessCode;
		this.studentAccessCode = studentAccessCode;
	}

	public setDesiredName(desiredName: string): Classroom {
		const newClassroom = new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.defaultSongIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			this.desiredClassroomAdminLimit,
			this.desiredStudentLimit,
			desiredName
		);
		newClassroom.classroomAdmins = this.classroomAdmins;
		newClassroom.students = this.students
		newClassroom.projects = this.projects;
		return newClassroom;
	}

	public setDesiredClassroomAdminLimit(desiredLimit: number): Classroom {
		const newClassroom = new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.defaultSongIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			desiredLimit,
			this.desiredStudentLimit,
			this.desiredName
		);
		newClassroom.classroomAdmins = this.classroomAdmins;
		newClassroom.students = this.students
		newClassroom.projects = this.projects;
		return newClassroom;
	}

	public setDesiredStudentLimit(desiredLimit: number): Classroom {
		const newClassroom = new Classroom(
			this.id,
			this.name,
			this.dateCreated,
			this.classroomAdminLimit,
			this.studentLimit,
			this.projectCount,
			this.submissionCount,
			this.tokens,
			this.defaultSongIds,
			this.classroomAdminAccessCode,
			this.studentAccessCode,
			this.desiredClassroomAdminLimit,
			desiredLimit,
			this.desiredName
		);
		newClassroom.classroomAdmins = this.classroomAdmins;
		newClassroom.students = this.students
		newClassroom.projects = this.projects;
		return newClassroom;
	}

	public loadProjects(projects: Project[]): void {
		this.projects = projects;
	}

	public getProjects(): Project[] | undefined {
		return this.projects;
	}

	public loadClassroomAdmins(classroomAdmins: User[]): void {
		this.classroomAdmins = classroomAdmins;
	}

	public getClassroomAdmins(): User[] | undefined {
		return this.classroomAdmins;
	}

	public loadClassroomStudents(classroomStudents: User[]): void {
		this.students = classroomStudents;
	}

	public getClassroomStudents(): User[] | undefined {
		return this.students;
	}

	public getRemainingActiveCredits(): number | undefined {
		return this.tokens?.filter((currentToken) => currentToken.isActive()).reduce((acc: number, token: ProjectSubmissionToken) => acc + token.getRemainingActiveCredits(), 0) ?? undefined;
	}

	public static fromJson(json: any): Classroom {
		return new Classroom(
			json.id,
			json.name,
			new Date(json.dateCreated),
			json.classroomAdminLimit,
			json.studentLimit,
			json.projectCount,
			json.submissionCount,
			json.tokens.map((token: any) => ProjectSubmissionToken.fromJson(token)),
			json.defaultSongIds,
			json.classroomAdminAccessCode,
			json.studentAccessCode
		);
	}
}

export class ProjectSubmissionToken {
	public readonly id: string;
	public readonly credits: number;
	public readonly dateCreated: Date;
	public readonly startDate: Date;
	public readonly expirationDate: Date;
	public readonly submissions: ProjectSubmission[];

	private constructor(
		id: string,
		credits: number,
		dateCreated: Date,
		startDate: Date,
		expirationDate: Date,
		submissions: ProjectSubmission[]
	) {
		this.id = id;
		this.credits = credits;
		this.dateCreated = dateCreated;
		this.startDate = startDate;
		this.expirationDate = expirationDate;
		this.submissions = submissions;
	}

	public getRemainingActiveCredits(): number {
		if (!this.isActive()) {
			return 0;
		}
		return this.credits - this.submissions.length;
	}

	public getActiveCredits(): number {
		if (!this.isActive()) {
			return 0;
		}
		return this.credits;
	}

	public passedStartDate() {
		// Get the current UTC date and time
		const nowUtc = new Date();
		return nowUtc >= this.startDate;
	}

	public isExpired() {
		// Get the current UTC date and time
		const nowUtc = new Date();
		return nowUtc > this.expirationDate;
	}

	public isActive() {
		return this.passedStartDate() && !this.isExpired();
	}

	public static fromJson(json: any): ProjectSubmissionToken {
		return new ProjectSubmissionToken(
			json.id,
			json.credits,
			new Date(json.created),
			new Date(json.start ?? 0),
			new Date(json.expiration),
			json.submissions
		);
	}
}
